<template>
  <div id="AccountPreferences" class="Main-Content">
    <ValidationObserver v-slot="{ invalid }" slim>
      <LoadingLayout v-if="loading" :page="true" />

      <Page
        v-else
        :title="$t('Preferences')"
        :primaryAction="{
          label: $t('Update'),
          onAction: save,
          disabled: saving || invalid
        }"
        :secondaryActions="[{
          label: $t('Notes'),
          onAction: () => eventHub.$emit('showAccountNotes', 'ACCOUNTPREFERENCES')
        }]"
      >

        <Layout>
          <LayoutSection>
            <Card>
              <CardSection>

                <FormLayout>
                  <FormLayoutGroup>
                    <ChoiceList
                      :label="$t('Campaign Updates')"
                      :help-text="$t('AccountPreferences.campaignUpdateHelp')"
                      :options="[
                        {
                          label: $t('Off'),
                          value: 0
                        },
                        {
                          label: $t('Weekly'),
                          value: 1
                        },
                        {
                          label: $t('Monthly'),
                          value: 2
                        },
                        {
                          label: $t('Weekly & Monthly'),
                          value: 3
                        }
                      ]"
                      v-model="prefs.campaignUpdateFrequency"
                    />

                    <EmailListInput
                      :emails.sync="prefs.campaignUpdateEmails"
                      :label="$t('Send Updates To')"
                      :add-surrounding-quotes="true"
                    />
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
              <CardSection v-if="prefs.campaignUpdateFrequency">
                <FormLayout>
                  <Select
                    :label="$t('Emails')"
                    v-model="account.splitWeeklyEmails"
                    help-text=""
                    :options="[
                      {
                        label: $t('Summarized for the account'),
                        value: false
                      },
                      {
                        label: $t('One per domain'),
                        value: true
                      },
                    ]"
                  />
                  <validation-provider :name="$t('AccountPreferences.template')" immediate rules="required" v-slot="{ errors }">
                    <Select
                      :label="$t('AccountPreferences.template')"
                      :placeholder="$t('AccountPreferences.selectTemplate')"
                      :options="[
                        {
                          label: $t('AccountPreferences.retail'),
                          value: 'retail'
                        },
                        {
                          label: $t('AccountPreferences.services'),
                          value: 'services'
                        }
                      ]"
                      v-model="prefs.campaignUpdateEmailSettings.template"
                      :error="errors[0]"
                    />
                  </validation-provider>
                  <FormLayoutGroup>
                    <validation-provider :name="$t('AccountPreferences.convertToCurrency')" immediate rules="required" v-slot="{ errors }">
                      <Select
                        :label="$t('AccountPreferences.convertToCurrency')"
                        :placeholder="$t('AccountPreferences.selectCurrency')"
                        :options="dropdownCurrencies"
                        :helpText="$t('AccountPreferences.defaultEmailSettingHelp')"
                        v-model="prefs.campaignUpdateEmailSettings.defaultConvertCurrency"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('AccountPreferences.emailLanguage')" immediate rules="required" v-slot="{ errors }">
                      <Select
                        :label="$t('AccountPreferences.emailLanguage')"
                        :placeholder="$t('AccountPreferences.selectLanguage')"
                        :options="supportedLanguages"
                        :helpText="$t('AccountPreferences.defaultEmailSettingHelp')"
                        v-model="prefs.campaignUpdateEmailSettings.defaultLanguage"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
              <CardSection v-if="prefs.campaignUpdateFrequency && account.splitWeeklyEmails">
                <DataTable
                  :columns="[{
                    key: 'domain',
                    label: $t('Domain')
                  },
                  {
                    key: 'currency',
                    label: $t('AccountPreferences.convertToCurrency')
                  },
                  {
                    key: 'language',
                    label: $t('AccountPreferences.emailLanguage')
                  }]"
                  :rows="formattedSites"
                  :row-actions="(site) => (getRowActions(site))"
                  :emptyText="$t('AccountPreferences.noDomains')"
                  compact
                >
                  <template v-slot:col:language="{row}">
                    <template v-if="row.language">{{row.language}}</template>
                    <Badge v-else>{{$t('Default')}} - {{getLanguageLabel(prefs.campaignUpdateEmailSettings.defaultLanguage)}}</Badge>
                  </template>
                  <template v-slot:col:currency="{row}">
                    <template v-if="row.currency">{{row.currency}}</template>
                    <Badge v-else>{{$t('Default')}} - {{prefs.campaignUpdateEmailSettings.defaultConvertCurrency || $t('N/A')}}</Badge>
                  </template>
                </DataTable>
              </CardSection>
            </Card>

            <Card>
              <CardSection>
                <FormLayoutGroup>
                  <TextField
                    :label="$t('Form Submission Emails Subject')"
                    v-model="prefs.formSubmitSubject"
                    :placeholder="$t('A new response from your Fanplayr campaign')"
                  />
                  <EmailListInput
                    :emails.sync="prefs.formSubmitEmails"
                    :label="$t('Send Submissions To')"
                    :add-surrounding-quotes="true"
                  />
                </FormLayoutGroup>
              </CardSection>
            </Card>

            <Card>
              <CardSection>
                <FormLayout>
                  <TextField
                    :label="$t('Currency')"
                    :value="account.currency"
                    disabled
                  />

                  <TimezonePicker
                    :label="$t('Timezone')"
                    :value="account.timezone"
                    readonly
                  />
                </FormLayout>

              </CardSection>
            </Card>

            <Card>
              <CardSection>
                <FormLayout>
                  <Checkbox
                    :label="$t('Collected Emails from orders')"
                    v-model="account.gatherSalesOrderEmails"
                    :help-text="$t('AccountPreferences.collectEmailsHelp')"
                  />
                </FormLayout>
              </CardSection>
            </Card>

            <Card>
              <CardSection>
                <FormLayout>
                  <Checkbox
                    :label="$t('Anonymize IPs')"
                    v-model="account.anonymizeIP"
                    :help-text="$t('AccountPreferences.anonymizeIpHelp')"
                  />
                </FormLayout>
              </CardSection>
            </Card>

            <!-- <Card>
              <CardSection>
                <Button type="primary" @click="save"></Button>
              </CardSection>
            </Card> -->
          </LayoutSection>
        </Layout>

        <Sheet
          :title="$t('AccountPreferences.domainEmailOptions')"
          :width="700"
          :visible="!!editSite"
          @close="closeEdit"
          padded
        >
          <template v-slot:actions>
            <Button type="primary" :disabled="saving" @click="setDomain">{{$t('Okay')}}</Button>
          </template>
          <Card v-if="editSite">
            <CardSection>
              <FormLayout>
                <Select
                  :label="$t('AccountPreferences.convertToCurrency')"
                  :placeholder="$t('AccountPreferences.selectCurrency')"
                  :options="currencies"
                  v-model="editSite.currency"
                  clearable
                />
                <Select
                  :label="$t('AccountPreferences.emailLanguage')"
                  :placeholder="$t('AccountPreferences.selectLanguage')"
                  :options="languages"
                  v-model="editSite.language"
                  clearable
                />
              </FormLayout>
            </CardSection>
          </Card>
        </Sheet>
      
      </Page>
    </ValidationObserver>

  </div>
</template>

<script>
import _ from 'lodash'

import EmailListInput from '../components/EmailListInput'

export default {
  name: 'AccountPreferences',
  data () {
    return {
      loading: true,
      saving: false,
      prefs: {},
      account: {},
      currencies: [],
      editSite: null,
      editSiteCopy: null,
      sites: [],

      backDoor: 0 // part of computed method hack
    }
  },
  mounted() {
    NProgress.start()
    Promise.all([
      this.api.getCurrentAccount(), 
      this.api.getCurrentAccountPrefs(), 
      this.api.getCurrenciesList(),
      this.api.getDomains()
    ])
      .then((data) => {
        this.account = data[0]
        this.prefs = data[1]
        this.currencies = data[2]
        this.sites = (data[3] || []).filter(d => !!d.campaignId)

        this.prefs.campaignUpdateFrequency = this.prefs.campaignUpdateFrequency || 0
        
        if (!this.prefs.campaignUpdateEmailSettings) this.prefs.campaignUpdateEmailSettings = {}
        this.prefs.campaignUpdateEmailSettings.template = this.prefs.campaignUpdateEmailSettings.template || 'retail'
        this.prefs.campaignUpdateEmailSettings.defaultConvertCurrency = this.prefs.campaignUpdateEmailSettings.defaultConvertCurrency || this.account.currency
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    formattedSites() {
      this.backDoor; // HACK: incrementing this value elsewhere forces this computed method to re-compute
      const domainSettings = this.prefs.campaignUpdateEmailSettings.domains || []
      return this.sites.map(s => {
        const setting = domainSettings.find(d => d.id === s.id)
        const langObj = setting ? this.supportedLanguages.find(l => l.value === setting.language) : null
        return {
          id: s.id,
          domain: s.domain,
          language: langObj && langObj.label || null,
          currency: setting && setting.currency || null
        }
      })
    },
    dropdownCurrencies() {
      const rv = this.currencies.filter(c => c.value !== this.account.currency)
      rv.unshift({
        value: this.account.currency,
        label: `${this.$t('Default')} (${this.account.currency})`
      })  
      return rv
    }
  },
  methods: {
    getLanguageLabel(value) {
      const langObj = this.supportedLanguages.find(l => l.value === value)
      return langObj ? langObj.label : this.$t('N/A')
    },
    editDomain(row) {
      const dom = (this.prefs.campaignUpdateEmailSettings.domains || []).find(d => d.id === row.id)
      this.editSite = _.cloneDeep(dom) || {
        id: row.id,
        language: null,
        currency: null
      }
      this.editSiteCopy = _.cloneDeep(this.editSite)
    },
    closeEdit() {
      this.editSite = null
    },
    setDomain() {
      if (!this.prefs.campaignUpdateEmailSettings.domains || !this.prefs.campaignUpdateEmailSettings.domains.length) {
        // if domains doesnt exist in preferences, then add it with this site as first value
        this.prefs.campaignUpdateEmailSettings.domains = [this.editSite]
        // this.$set(this, 'prefs.campaignUpdateEmailSettings.domains', [this.editSite])
      } else if (!this.prefs.campaignUpdateEmailSettings.domains.find(d => d.id === this.editSite.id)) {
        // if domains exists in preferences but this specific domain isnt in the list, add it
        this.prefs.campaignUpdateEmailSettings.domains.push(this.editSite)
      } else {
        // if domain is already in list, change the current values
        const domainSetting = this.prefs.campaignUpdateEmailSettings.domains.find(d => d.id === this.editSite.id)
        domainSetting.language = this.editSite.language
        domainSetting.currency = this.editSite.currency
      }
      this.editSite = null
      this.backDoor++; // force computed method to recompute
    },
    getRowActions(site) {
      return [{
        label: this.$t('Edit'),
        onAction: () => this.editDomain(site)
      }]
    },
    save() {
      NProgress.start()
      this.saving = true
      if (!this.prefs.campaignUpdateFrequency) this.prefs.campaignUpdateEmailSettings = {}
      if (!this.account.splitWeeklyEmails) this.prefs.campaignUpdateEmailSettings.domains = []
      this.api.updateCurrentAccountPrefs({
          campaignUpdateFrequency: this.prefs.campaignUpdateFrequency,
          campaignUpdateEmails: this.prefs.campaignUpdateEmails,
          formSubmitSubject: this.prefs.formSubmitSubject,
          formSubmitEmails: this.prefs.formSubmitEmails
        },
        {
          template: this.prefs.campaignUpdateEmailSettings.template || null,
          defaultConvertCurrency: this.prefs.campaignUpdateEmailSettings.defaultConvertCurrency || null,
          defaultLanguage: this.prefs.campaignUpdateEmailSettings.defaultLanguage || null,
          domains: this.prefs.campaignUpdateEmailSettings.domains || []
        },
        this.account.anonymizeIP,
        this.account.gatherSalesOrderEmails,
        this.account.splitWeeklyEmails
      )
        .then(() => {
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    }
  },
  components: {
    EmailListInput
  }
}

</script>